import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import SafeChart from '../components/SafeChart';
import AthleteContextHeader from '../components/AthleteContextHeader';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { shouldLimitForContractFixture } from '../lib/visibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Technical', 'Tactical', 'Physical', 'Mental', 'Social'];

const PDPPage = ({ athleteId: propAthleteId }) => {
  const { user } = useAuth();
  const isScopedMode = !!propAthleteId;
  
  // State
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('goals');
  const [access, setAccess] = useState({ can_view: false, can_edit: false, can_self_eval: false });
  const [lastMatchRating, setLastMatchRating] = useState(null);
  
  // Dialog states
  const [createCycleDialog, setCreateCycleDialog] = useState(false);
  const [createGoalDialog, setCreateGoalDialog] = useState(false);
  const [editGoalDialog, setEditGoalDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [selfEvalDialog, setSelfEvalDialog] = useState(false);
  
  // Form states
  const [newCycle, setNewCycle] = useState({ name: '', start_date: '', end_date: '', description: '' });
  const [newGoal, setNewGoal] = useState({ category: '', title: '', description: '', target_date: '', priority: 'medium' });
  const [editingGoal, setEditingGoal] = useState(null);
  const [newFeedback, setNewFeedback] = useState({ feedback_text: '', areas_of_strength: '', areas_to_improve: '', recommendations: '' });
  const [selfEval, setSelfEval] = useState({ feedback_text: '', rating: 5, goals_reflection: '', challenges_faced: '', next_steps: '' });

  // Fetch athletes on mount
  useEffect(() => {
    if (propAthleteId) {
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => {
        setAthletes([res.data]);
        setSelectedAthlete(res.data.id);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      fetchAthletes();
    }
  }, [propAthleteId]);

  // Fetch cycles when athlete changes
  useEffect(() => {
    if (selectedAthlete) {
      // Reset stale data before loading new athlete
      setCycleData(null);
      setActiveCycle(null);
      setCycles([]);
      setLastMatchRating(null);
      fetchAthleteCycles(selectedAthlete);
      // Fetch last match rating
      axios.get(`${API}/match-reports-v2/reports/athlete/${selectedAthlete}`)
        .then(res => {
          const reps = res.data?.reports || [];
          if (reps.length > 0 && reps[0]?.coach_evaluation) {
            setLastMatchRating(reps[0].coach_evaluation.overall_rating);
          } else {
            setLastMatchRating(null);
          }
        })
        .catch(() => setLastMatchRating(null));
    }
  }, [selectedAthlete]);

  // Fetch cycle details when active cycle changes
  const fetchCycleDetails = useCallback(async (cycleId) => {
    try {
      const response = await axios.get(`${API}/pdp/cycles/${cycleId}`);
      setCycleData(response.data);
      setAccess(response.data.access || { can_view: true, can_edit: false, can_self_eval: false });
    } catch (error) {
      toast.error('Failed to load cycle details');
    }
  }, []);

  useEffect(() => {
    if (activeCycle?.id) {
      fetchCycleDetails(activeCycle.id);
    }
  }, [activeCycle, fetchCycleDetails]);

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/athletes`);
      // API returns array directly, not {athletes: [...]}
      const athletesList = Array.isArray(response.data) ? response.data : (response.data.athletes || []);
      setAthletes(athletesList);
    } catch (error) {
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteCycles = async (athleteId) => {
    try {
      const response = await axios.get(`${API}/pdp/cycles/athlete/${athleteId}`);
      let list = response.data.cycles || [];
      // UX guard: Admin/Staff viewing Test Player (contract fixture) see only
      // the 3 most recent cycles. Player/Parent keep full history.
      if (shouldLimitForContractFixture(athleteId) && list.length > 3) {
        list = [...list]
          .sort((a, b) => new Date(b.start_date || b.created_at || 0).getTime()
            - new Date(a.start_date || a.created_at || 0).getTime())
          .slice(0, 3);
      }
      setCycles(list);
      setActiveCycle(response.data.active_cycle);
      setAccess(response.data.access || { can_view: true, can_edit: false, can_self_eval: false });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this athlete\'s PDP');
        setCycles([]);
        setActiveCycle(null);
      } else {
        toast.error('Failed to load PDP cycles');
      }
    }
  };

  // Create cycle
  const handleCreateCycle = async () => {
    if (!newCycle.name || !newCycle.start_date || !newCycle.end_date) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const response = await axios.post(`${API}/pdp/cycles`, {
        athlete_id: selectedAthlete,
        ...newCycle
      });
      toast.success('PDP cycle created');
      setCreateCycleDialog(false);
      setNewCycle({ name: '', start_date: '', end_date: '', description: '' });
      fetchAthleteCycles(selectedAthlete);
      // Set newly created cycle as active
      if (response.data.cycle) {
        setActiveCycle(response.data.cycle);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cycle');
    }
  };

  // Create goal
  const handleCreateGoal = async () => {
    if (!newGoal.category || !newGoal.title) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/pdp/goals`, {
        cycle_id: activeCycle.id,
        athlete_id: selectedAthlete,
        ...newGoal
      });
      toast.success('Goal created');
      setCreateGoalDialog(false);
      setNewGoal({ category: '', title: '', description: '', target_date: '', priority: 'medium' });
      fetchCycleDetails(activeCycle.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create goal');
    }
  };

  // Update goal
  const handleUpdateGoal = async () => {
    if (!editingGoal) return;
    try {
      await axios.put(`${API}/pdp/goals/${editingGoal.id}`, {
        title: editingGoal.title,
        description: editingGoal.description,
        target_date: editingGoal.target_date,
        priority: editingGoal.priority,
        status: editingGoal.status,
        progress: editingGoal.progress
      });
      toast.success('Goal updated');
      setEditGoalDialog(false);
      setEditingGoal(null);
      fetchCycleDetails(activeCycle.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update goal');
    }
  };

  // Delete goal
  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await axios.delete(`${API}/pdp/goals/${goalId}`);
      toast.success('Goal deleted');
      fetchCycleDetails(activeCycle.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete goal');
    }
  };

  // Add coach feedback
  const handleAddFeedback = async () => {
    if (!newFeedback.feedback_text) {
      toast.error('Please enter feedback');
      return;
    }
    try {
      await axios.post(`${API}/pdp/feedback`, {
        cycle_id: activeCycle.id,
        athlete_id: selectedAthlete,
        ...newFeedback
      });
      toast.success('Feedback added');
      setFeedbackDialog(false);
      setNewFeedback({ feedback_text: '', areas_of_strength: '', areas_to_improve: '', recommendations: '' });
      fetchCycleDetails(activeCycle.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add feedback');
    }
  };

  // Submit self-evaluation
  const handleSelfEvaluation = async () => {
    if (!selfEval.feedback_text || !selfEval.rating) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/pdp/self-evaluation`, {
        cycle_id: activeCycle.id,
        athlete_id: selectedAthlete,
        ...selfEval
      });
      toast.success('Self-evaluation submitted');
      setSelfEvalDialog(false);
      setSelfEval({ feedback_text: '', rating: 5, goals_reflection: '', challenges_faced: '', next_steps: '' });
      fetchCycleDetails(activeCycle.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit evaluation');
    }
  };

  // Complete cycle
  const handleCompleteCycle = async () => {
    if (!window.confirm('Mark this PDP cycle as completed?')) return;
    try {
      await axios.put(`${API}/pdp/cycles/${activeCycle.id}?status=completed`);
      toast.success('Cycle marked as completed');
      fetchAthleteCycles(selectedAthlete);
    } catch (error) {
      toast.error('Failed to complete cycle');
    }
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'not_started': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Technical': return '⚽';
      case 'Tactical': return '🧠';
      case 'Physical': return '💪';
      case 'Mental': return '🧘';
      case 'Social': return '🤝';
      default: return '📋';
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="pdp-page">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Development Plan" />
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <img src="/pfap-logo-icon.png" alt="PFAP" className="h-10 w-10 hidden sm:block" />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Player Development Plan
              </h2>
              <p className="text-gray-600 dark:text-gray-500 mt-1 text-sm">Manage athlete development goals and track progress</p>
            </div>
          </div>
        </div>

        {/* Athlete & Cycle Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px] max-w-[300px]">
                <Label>Athlete</Label>
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={selectedAthlete}
                  onChange={setSelectedAthlete}
                  placeholder="Choose an athlete..."
                  disabled={isScopedMode}
                  data-testid="athlete-select"
                />
              </div>
              
              {selectedAthlete && cycles.length > 0 && (
                <div className="flex-1 min-w-[200px] max-w-[300px]">
                  <Label>PDP Cycle</Label>
                  <Select 
                    value={activeCycle?.id || ''} 
                    onValueChange={(id) => setActiveCycle(cycles.find(c => c.id === id))}
                  >
                    <SelectTrigger data-testid="cycle-select">
                      <SelectValue placeholder="Select cycle..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cycles.map(cycle => (
                        <SelectItem key={cycle.id} value={cycle.id}>
                          {cycle.name} {cycle.status === 'active' && '(Active)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {access.can_edit && selectedAthlete && (
                <Button 
                  onClick={() => setCreateCycleDialog(true)}
                  className="bg-pfap-green hover:bg-pfap-green/90"
                  data-testid="create-cycle-btn"
                >
                  + New Cycle
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        {!selectedAthlete ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              Select an athlete to view their development plan
            </CardContent>
          </Card>
        ) : !activeCycle ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              {cycles.length === 0 ? (
                <>
                  No PDP cycles found for this athlete.
                  {access.can_edit && ' Click "New Cycle" to create one.'}
                </>
              ) : (
                'Select a PDP cycle to view details'
              )}
            </CardContent>
          </Card>
        ) : cycleData ? (
          <>
            {/* Progress Overview */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{activeCycle.name}</CardTitle>
                    <CardDescription>
                      {new Date(activeCycle.start_date).toLocaleDateString()} - {new Date(activeCycle.end_date).toLocaleDateString()}
                      {activeCycle.status === 'completed' && (
                        <Badge className="ml-2 bg-emerald-100 text-emerald-800">Completed</Badge>
                      )}
                    </CardDescription>
                  </div>
                  {access.can_edit && activeCycle.status === 'active' && (
                    <Button variant="outline" size="sm" onClick={handleCompleteCycle}>
                      Mark Complete
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {/* Overall Progress */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Overall Progress</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        {cycleData.progress?.overall_progress || 0}%
                      </span>
                    </div>
                    <Progress value={cycleData.progress?.overall_progress || 0} className="h-2" />
                  </div>
                  
                  {/* Goals Summary */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Goals</span>
                    <div className="flex gap-4 mt-2">
                      <div className="text-center">
                        <span className="text-xl font-bold text-gray-900 dark:text-gray-100">{cycleData.progress?.total_goals || 0}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Total</p>
                      </div>
                      <div className="text-center">
                        <span className="text-xl font-bold text-emerald-600">{cycleData.progress?.completed_goals || 0}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Done</p>
                      </div>
                      <div className="text-center">
                        <span className="text-xl font-bold text-blue-600">{cycleData.progress?.in_progress_goals || 0}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Active</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feedback Count */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Feedback & Evaluations</span>
                    <div className="flex gap-4 mt-2">
                      <div className="text-center">
                        <span className="text-xl font-bold text-purple-600">{cycleData.feedback?.length || 0}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Coach</p>
                      </div>
                      <div className="text-center">
                        <span className="text-xl font-bold text-amber-600">{cycleData.self_evaluations?.length || 0}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Self</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Category Progress + Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {CATEGORIES.map(cat => {
                      const catProgress = cycleData.progress?.by_category?.[cat] || { progress: 0, goal_count: 0 };
                      return (
                        <div key={cat} className="p-3 bg-white dark:bg-gray-800 border rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <span>{getCategoryIcon(cat)}</span>
                            <span className="text-xs font-medium">{cat}</span>
                          </div>
                          <Progress value={catProgress.progress} className="h-1.5 mb-1" />
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500">
                            <span>{catProgress.goal_count} goals</span>
                            <span>{catProgress.progress}%</span>
                          </div>
                        </div>
                      );
                    })}
                    {/* Last Match Rating Card */}
                    {lastMatchRating !== null && (
                      <div className="p-3 bg-white dark:bg-gray-800 border rounded-lg border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span>📋</span>
                          <span className="text-xs font-medium">Last Match</span>
                        </div>
                        <p className={`text-2xl font-bold ${lastMatchRating >= 7 ? 'text-emerald-600' : lastMatchRating >= 5 ? 'text-amber-600' : 'text-red-600'}`}>
                          {lastMatchRating}/10
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Coach Rating</p>
                      </div>
                    )}
                  </div>
                  {/* Category Comparison Chart */}
                  <div data-testid="pdp-category-chart">
                    <SafeChart height={200}>
                      <BarChart data={CATEGORIES.map(cat => ({
                        category: cat,
                        progress: cycleData?.progress?.by_category?.[cat]?.progress || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="category" tick={{fontSize: 11}} />
                        <YAxis domain={[0, 100]} tick={{fontSize: 11}} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Progress']} />
                        <Bar dataKey="progress" radius={[4, 4, 0, 0]}>
                          {CATEGORIES.map((cat, i) => {
                            const colors = ['#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b', '#10b981'];
                            return <Cell key={cat} fill={colors[i]} />;
                          })}
                        </Bar>
                      </BarChart>
                    </SafeChart>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="goals">Goals</TabsTrigger>
                <TabsTrigger value="feedback">Feedback</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              {/* Goals Tab */}
              <TabsContent value="goals">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Development Goals</CardTitle>
                      {access.can_edit && activeCycle.status === 'active' && (
                        <Button 
                          onClick={() => setCreateGoalDialog(true)}
                          className="bg-pfap-green hover:bg-pfap-green/90"
                          data-testid="create-goal-btn"
                        >
                          + Add Goal
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(cycleData.goals || []).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-500 py-8">
                        No goals in this cycle yet.
                        {access.can_edit && ' Click "Add Goal" to create one.'}
                      </p>
                    ) : (
                      <div className="space-y-6">
                        {CATEGORIES.map(category => {
                          const rawGoals = cycleData.goals_by_category?.[category] || [];
                          // UX guard: Admin/Staff viewing Test Player see only 3 most
                          // recent goals per category. Player/Parent keep full list.
                          const limit = shouldLimitForContractFixture(selectedAthlete);
                          const categoryGoals = limit
                            ? [...rawGoals]
                                .sort((a, b) => new Date(b.created_at || 0).getTime()
                                  - new Date(a.created_at || 0).getTime())
                                .slice(0, 3)
                            : rawGoals;
                          if (categoryGoals.length === 0) return null;
                          const hiddenCount = limit ? Math.max(0, rawGoals.length - categoryGoals.length) : 0;
                          
                          return (
                            <div key={category}>
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-lg">{getCategoryIcon(category)}</span>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{category}</h3>
                                <Badge variant="outline">{rawGoals.length}</Badge>
                                {hiddenCount > 0 && (
                                  <span className="text-xs text-muted-foreground">· showing 3 most recent</span>
                                )}
                              </div>
                              <div className="space-y-3 ml-7">
                                {categoryGoals.map(goal => (
                                  <div 
                                    key={goal.id}
                                    className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                                    data-testid={`goal-${goal.id}`}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-medium">{goal.title}</span>
                                          <Badge className={getStatusColor(goal.status)}>
                                            {goal.status?.replace('_', ' ')}
                                          </Badge>
                                          <Badge className={getPriorityColor(goal.priority)}>
                                            {goal.priority}
                                          </Badge>
                                        </div>
                                        {goal.description && (
                                          <p className="text-sm text-gray-600 dark:text-gray-500 mb-2">{goal.description}</p>
                                        )}
                                        <div className="flex items-center gap-4">
                                          <div className="flex-1 max-w-xs">
                                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mb-1">
                                              <span>Progress</span>
                                              <span>{goal.progress}%</span>
                                            </div>
                                            <Progress value={goal.progress} className="h-2" />
                                          </div>
                                          {goal.target_date && (
                                            <span className="text-xs text-gray-500 dark:text-gray-500">
                                              Due: {new Date(goal.target_date).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {access.can_edit && activeCycle.status === 'active' && (
                                        <div className="flex gap-2 ml-4">
                                          <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => {
                                              setEditingGoal(goal);
                                              setEditGoalDialog(true);
                                            }}
                                          >
                                            Edit
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => handleDeleteGoal(goal.id)}
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Feedback Tab */}
              <TabsContent value="feedback">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Coach Feedback */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Coach Feedback</CardTitle>
                        {access.can_edit && activeCycle.status === 'active' && (
                          <Button 
                            size="sm"
                            onClick={() => setFeedbackDialog(true)}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            + Add Feedback
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(cycleData.feedback || []).length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-500 py-6">No coach feedback yet</p>
                      ) : (
                        <div className="space-y-4">
                          {cycleData.feedback.map(fb => (
                            <div key={fb.id} className="p-4 bg-purple-50 rounded-lg">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-purple-900">{fb.coach_name}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                  {new Date(fb.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{fb.feedback_text}</p>
                              {fb.areas_of_strength && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-emerald-600">Strengths:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{fb.areas_of_strength}</p>
                                </div>
                              )}
                              {fb.areas_to_improve && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-amber-600">Areas to Improve:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{fb.areas_to_improve}</p>
                                </div>
                              )}
                              {fb.recommendations && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-blue-600">Recommendations:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{fb.recommendations}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Player Self-Evaluation */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Player Self-Evaluation</CardTitle>
                        {(access.can_self_eval || access.can_edit) && activeCycle.status === 'active' && (
                          <Button 
                            size="sm"
                            onClick={() => setSelfEvalDialog(true)}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            + Submit Evaluation
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(cycleData.self_evaluations || []).length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-500 py-6">No self-evaluations yet</p>
                      ) : (
                        <div className="space-y-4">
                          {cycleData.self_evaluations.map(se => (
                            <div key={se.id} className="p-4 bg-amber-50 rounded-lg">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-amber-900">{se.player_name}</span>
                                  <Badge className="bg-amber-100 text-amber-800">
                                    Rating: {se.rating}/10
                                  </Badge>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                  {new Date(se.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{se.feedback_text}</p>
                              {se.goals_reflection && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-blue-600">Goals Reflection:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{se.goals_reflection}</p>
                                </div>
                              )}
                              {se.challenges_faced && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-red-600">Challenges:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{se.challenges_faced}</p>
                                </div>
                              )}
                              {se.next_steps && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-emerald-600">Next Steps:</span>
                                  <p className="text-xs text-gray-600 dark:text-gray-500">{se.next_steps}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history">
                <Card>
                  <CardHeader>
                    <CardTitle>PDP History</CardTitle>
                    <CardDescription>Previous development cycles and their outcomes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PDPHistory athleteId={selectedAthlete} currentCycleId={activeCycle?.id} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* Create Cycle Dialog */}
        <Dialog open={createCycleDialog} onOpenChange={setCreateCycleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create PDP Cycle</DialogTitle>
              <DialogDescription>Start a new development cycle for this athlete</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Cycle Name *</Label>
                <Input 
                  value={newCycle.name}
                  onChange={(e) => setNewCycle({...newCycle, name: e.target.value})}
                  placeholder="e.g., Q1 2026, Season 2025-26"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input 
                    type="date"
                    value={newCycle.start_date}
                    onChange={(e) => setNewCycle({...newCycle, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input 
                    type="date"
                    value={newCycle.end_date}
                    onChange={(e) => setNewCycle({...newCycle, end_date: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newCycle.description}
                  onChange={(e) => setNewCycle({...newCycle, description: e.target.value})}
                  placeholder="Optional description for this cycle..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateCycleDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateCycle} className="bg-pfap-green hover:bg-pfap-green/90">
                Create Cycle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Goal Dialog */}
        <Dialog open={createGoalDialog} onOpenChange={setCreateGoalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Development Goal</DialogTitle>
              <DialogDescription>Create a new goal for this PDP cycle</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Category *</Label>
                <Select value={newGoal.category} onValueChange={(v) => setNewGoal({...newGoal, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input 
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  placeholder="e.g., Improve passing accuracy to 85%"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  placeholder="Detailed description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select value={newGoal.priority} onValueChange={(v) => setNewGoal({...newGoal, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Date</Label>
                  <Input 
                    type="date"
                    value={newGoal.target_date}
                    onChange={(e) => setNewGoal({...newGoal, target_date: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateGoalDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateGoal} className="bg-pfap-green hover:bg-pfap-green/90">
                Create Goal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Goal Dialog */}
        <Dialog open={editGoalDialog} onOpenChange={setEditGoalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Goal</DialogTitle>
            </DialogHeader>
            {editingGoal && (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={editingGoal.title}
                    onChange={(e) => setEditingGoal({...editingGoal, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={editingGoal.description || ''}
                    onChange={(e) => setEditingGoal({...editingGoal, description: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={editingGoal.status} 
                      onValueChange={(v) => setEditingGoal({...editingGoal, status: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select 
                      value={editingGoal.priority} 
                      onValueChange={(v) => setEditingGoal({...editingGoal, priority: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Progress: {editingGoal.progress}%</Label>
                  <Slider 
                    value={[editingGoal.progress]}
                    onValueChange={([v]) => setEditingGoal({...editingGoal, progress: v})}
                    max={100}
                    step={5}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Target Date</Label>
                  <Input 
                    type="date"
                    value={editingGoal.target_date || ''}
                    onChange={(e) => setEditingGoal({...editingGoal, target_date: e.target.value})}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditGoalDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateGoal} className="bg-pfap-green hover:bg-pfap-green/90">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Feedback Dialog */}
        <Dialog open={feedbackDialog} onOpenChange={setFeedbackDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Coach Feedback</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Feedback *</Label>
                <Textarea 
                  value={newFeedback.feedback_text}
                  onChange={(e) => setNewFeedback({...newFeedback, feedback_text: e.target.value})}
                  placeholder="Overall feedback on the athlete's progress..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Areas of Strength</Label>
                <Textarea 
                  value={newFeedback.areas_of_strength}
                  onChange={(e) => setNewFeedback({...newFeedback, areas_of_strength: e.target.value})}
                  placeholder="What the athlete is doing well..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Areas to Improve</Label>
                <Textarea 
                  value={newFeedback.areas_to_improve}
                  onChange={(e) => setNewFeedback({...newFeedback, areas_to_improve: e.target.value})}
                  placeholder="What needs more work..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Recommendations</Label>
                <Textarea 
                  value={newFeedback.recommendations}
                  onChange={(e) => setNewFeedback({...newFeedback, recommendations: e.target.value})}
                  placeholder="Specific recommendations for improvement..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFeedbackDialog(false)}>Cancel</Button>
              <Button onClick={handleAddFeedback} className="bg-purple-600 hover:bg-purple-700">
                Submit Feedback
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Self-Evaluation Dialog */}
        <Dialog open={selfEvalDialog} onOpenChange={setSelfEvalDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Self-Evaluation</DialogTitle>
              <DialogDescription>Reflect on your progress in this PDP cycle</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Overall Rating *</Label>
                <div className="flex items-center gap-4 mt-2">
                  <Slider 
                    value={[selfEval.rating]}
                    onValueChange={([v]) => setSelfEval({...selfEval, rating: v})}
                    min={1}
                    max={10}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-2xl font-bold text-amber-600 w-12 text-center">
                    {selfEval.rating}
                  </span>
                </div>
              </div>
              <div>
                <Label>Self-Assessment *</Label>
                <Textarea 
                  value={selfEval.feedback_text}
                  onChange={(e) => setSelfEval({...selfEval, feedback_text: e.target.value})}
                  placeholder="How do you feel about your progress?"
                  rows={3}
                />
              </div>
              <div>
                <Label>Goals Reflection</Label>
                <Textarea 
                  value={selfEval.goals_reflection}
                  onChange={(e) => setSelfEval({...selfEval, goals_reflection: e.target.value})}
                  placeholder="How well did you work towards your goals?"
                  rows={2}
                />
              </div>
              <div>
                <Label>Challenges Faced</Label>
                <Textarea 
                  value={selfEval.challenges_faced}
                  onChange={(e) => setSelfEval({...selfEval, challenges_faced: e.target.value})}
                  placeholder="What difficulties did you encounter?"
                  rows={2}
                />
              </div>
              <div>
                <Label>Next Steps</Label>
                <Textarea 
                  value={selfEval.next_steps}
                  onChange={(e) => setSelfEval({...selfEval, next_steps: e.target.value})}
                  placeholder="What do you want to focus on next?"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelfEvalDialog(false)}>Cancel</Button>
              <Button onClick={handleSelfEvaluation} className="bg-amber-600 hover:bg-amber-700">
                Submit Evaluation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// PDP History Component
const PDPHistory = ({ athleteId, currentCycleId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (athleteId) {
      fetchHistory();
    }
  }, [athleteId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pdp/history/athlete/${athleteId}`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <p className="text-center text-gray-500 dark:text-gray-500 py-8">
        No completed PDP cycles yet
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {history.map(item => (
        <div 
          key={item.cycle.id}
          className={`p-4 rounded-lg border ${
            item.cycle.id === currentCycleId ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
          }`}
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className="font-semibold">{item.cycle.name}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {new Date(item.cycle.start_date).toLocaleDateString()} - {new Date(item.cycle.end_date).toLocaleDateString()}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-600">
                {item.goals_summary.final_score}%
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500">Final Score</p>
            </div>
          </div>
          
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-500">Goals:</span>{' '}
              <span className="font-medium">{item.goals_summary.completed}/{item.goals_summary.total} completed</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-500">Coach Feedback:</span>{' '}
              <span className="font-medium">{item.feedback_count}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-500">Self-Evals:</span>{' '}
              <span className="font-medium">{item.self_eval_count}</span>
            </div>
          </div>
          
          {/* Category breakdown */}
          <div className="mt-3 flex gap-2 flex-wrap">
            {CATEGORIES.map(cat => {
              const catData = item.progress_by_category?.[cat];
              if (!catData || catData.goal_count === 0) return null;
              return (
                <Badge key={cat} variant="outline" className="text-xs">
                  {cat}: {catData.progress}%
                </Badge>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PDPPage;
